import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import {
  appendUniqueMessage,
  conversationToContact,
  messageToBubble,
  queryToPeerIntent,
} from './messagesModel';
import {
  parseAttachmentMessage,
  parseLocationMessage,
  reverseGeocodeLocation,
} from './messagesParsers';
import { connectMessagesRealtime } from './messagesRealtime';
import './Messages.css';

const STORAGE = {
  safetyAck: 'sx_messages_safety_ack_v1',
};

const COMPOSER_TOOLS = [
  { type: 'smile', label: '表情' },
  { type: 'image', label: '图片' },
  { type: 'scissors', label: '剪切' },
  { type: 'clipboard', label: '文件' },
  { type: 'pin', label: '位置' },
];

const emojiOptions = ['🙂', '👍', '🙏', '👌', '🎉', '💡', '📌', '✅'];

const MESSAGE_TEXT = {
  zh: {
    title: '消息',
    reset: '重新加载',
    search: '搜索联系人...',
    noContact: '没有匹配的联系人',
    clearConfirm: '重新从服务器加载会话列表？',
    reloadNow: '确认重新加载',
    cancel: '取消',
    toolUnavailable: label => `${label}功能暂未接入`,
    uploadingAttachment: '正在上传附件...',
    uploadFailed: '附件上传失败，请稍后重试',
    screenCaptureFailed: '无法完成截图，请允许浏览器共享屏幕或窗口',
    cropTitle: '截图预览',
    cropRetake: '重选',
    sendScreenshot: '发送截图',
    locationFailed: '无法获取当前位置，请检查浏览器定位权限',
    locationCardTitle: '当前位置',
    locationCardHint: '点击打开地图',
    locationAttribution: '地址来自 OpenStreetMap',
    imageMessage: (name, url) => `【图片】${name}\n${url}`,
    fileMessage: (name, url) => `【文件】${name}\n${url}`,
    screenshotMessage: (name, url) => `【截图】${name}\n${url}`,
    locationMessage: (url, address) => address ? `【位置】${address}\n${url}` : `【位置】${url}`,
    tools: { 表情: '表情', 图片: '图片', 剪切: '剪切', 文件: '文件', 位置: '位置' },
    toolAria: '消息工具',
    input: '请输入消息，按 Enter 键发送或点击发送按钮发送',
    send: '发送',
    sending: '发送中...',
    empty: '选择一个会话开始聊天',
    loading: '正在加载会话...',
    loadingMessages: '正在加载消息...',
    noMessages: '还没有消息',
    loadFailed: '消息加载失败，请稍后重试',
    sendFailed: '发送失败，请稍后重试',
    createFailed: '会话创建失败，请稍后重试',
    displayOnlyNotice: name => `${name} 暂未绑定真实用户，不能创建会话。请从已发布的服务或商品入口联系。`,
    safetyTitle: '遵守Oblitrok规则，确保自身安全',
    safetyIntro: '签订合同前，请勿分享个人联系信息或外部链接。合同生效后，信息分享应仅限于必要范围。',
    safetyPay: '付款必须始终在Oblitrok平台上完成。',
    safetyPenalty: '违反这些规则可能导致账号受限。',
    learnMore: '查看平台说明',
    agreement: '我理解并同意根据用户协议在Oblitrok上保留付款和合同前聊天记录。',
    confirm: '确认',
    verified: '已认证',
    dealStatus: '项目沟通中',
    contactSupport: '联系客服',
    supportChat: '客服支持',
    more: '更多',
  },
  en: {
    title: 'Messages',
    reset: 'Reload',
    search: 'Search contacts...',
    noContact: 'No matching contacts',
    clearConfirm: 'Reload conversations from the server?',
    reloadNow: 'Reload now',
    cancel: 'Cancel',
    toolUnavailable: label => `${label} is not connected yet`,
    uploadingAttachment: 'Uploading attachment...',
    uploadFailed: 'Attachment upload failed. Please try again later.',
    screenCaptureFailed: 'Cannot capture the screen. Allow screen sharing and try again.',
    cropTitle: 'Screenshot preview',
    cropRetake: 'Retake',
    sendScreenshot: 'Send screenshot',
    locationFailed: 'Cannot get your current location. Check browser location permission.',
    locationCardTitle: 'Shared location',
    locationCardHint: 'Open in maps',
    locationAttribution: 'Address from OpenStreetMap',
    imageMessage: (name, url) => `[Image] ${name}\n${url}`,
    fileMessage: (name, url) => `[File] ${name}\n${url}`,
    screenshotMessage: (name, url) => `[Screenshot] ${name}\n${url}`,
    locationMessage: (url, address) => address ? `[Location] ${address}\n${url}` : `[Location] ${url}`,
    tools: { 表情: 'Emoji', 图片: 'Image', 剪切: 'Clip', 文件: 'File', 位置: 'Location' },
    toolAria: 'Message tools',
    input: 'Type a message. Press Enter or click Send.',
    send: 'Send',
    sending: 'Sending...',
    empty: 'Select a conversation to start chatting',
    loading: 'Loading conversations...',
    loadingMessages: 'Loading messages...',
    noMessages: 'No messages yet',
    loadFailed: 'Failed to load messages. Please try again later.',
    sendFailed: 'Failed to send. Please try again later.',
    createFailed: 'Failed to create conversation. Please try again later.',
    displayOnlyNotice: name => `${name} is not linked to a real user yet, so a conversation cannot be created. Contact from a published service or product entry.`,
    safetyTitle: 'Stay safe on Oblitrok by following the rules',
    safetyIntro: 'Do not share personal contact information or external links before signing a contract. Once a contract is in place, keep information sharing within the necessary scope.',
    safetyPay: 'Payments must always be completed on Oblitrok.',
    safetyPenalty: 'Violations of these rules may result in account restrictions.',
    learnMore: 'View platform guide',
    agreement: 'I understand and agree to keep payments and pre-contract chats on Oblitrok per the User Agreement.',
    confirm: 'Confirm',
    verified: 'Verified',
    dealStatus: 'Project chat',
    contactSupport: 'Contact support',
    supportChat: 'Support',
    unavailable: 'Not connected yet',
    more: 'More',
  },
};

function ComposerIcon({ type }) {
  const common = {
    width: '21',
    height: '21',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  if (type === 'image') {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9" r="1.5" />
        <path d="M21 15l-5-5L5 20" />
      </svg>
    );
  }

  if (type === 'scissors') {
    return (
      <svg {...common}>
        <circle cx="6" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M20 4L8.1 15.9" />
        <path d="M14.5 14.5L20 20" />
        <path d="M8.1 8.1L12 12" />
      </svg>
    );
  }

  if (type === 'clipboard') {
    return (
      <svg {...common}>
        <path d="M9 4h6a2 2 0 0 1 2 2v1H7V6a2 2 0 0 1 2-2Z" />
        <path d="M7 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-1" />
      </svg>
    );
  }

  if (type === 'pin') {
    return (
      <svg {...common}>
        <path d="M20 10c0 5.5-8 11-8 11S4 15.5 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <path d="M9 9h.01" />
      <path d="M15 9h.01" />
    </svg>
  );
}

function apiMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}

const DEFAULT_SCREENSHOT_CROP = { x: 0, y: 0, width: 1, height: 1 };

function clampUnit(value) {
  return Math.min(1, Math.max(0, value));
}

function cropFromPoints(start, end) {
  const x1 = clampUnit(start.x);
  const y1 = clampUnit(start.y);
  const x2 = clampUnit(end.x);
  const y2 = clampUnit(end.y);

  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

export default function Messages() {
  const { language, translatePhrase } = useLanguage();
  const { user } = useAuth() || {};
  const text = MESSAGE_TEXT[language] || MESSAGE_TEXT.en;
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [active, setActive] = useState(null);
  const [input, setInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [error, setError] = useState('');
  const [pageNotice, setPageNotice] = useState('');
  const [composerNotice, setComposerNotice] = useState('');
  const [reloadConfirmVisible, setReloadConfirmVisible] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showSafety, setShowSafety] = useState(() => localStorage.getItem(STORAGE.safetyAck) !== '1');
  const [safetyChecked, setSafetyChecked] = useState(false);
  const [screenshotDraft, setScreenshotDraft] = useState(null);
  const [screenshotCrop, setScreenshotCrop] = useState(DEFAULT_SCREENSHOT_CROP);
  const msgAreaRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cropStageRef = useRef(null);
  const cropDragRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const activeRef = useRef(null);
  const cursorRef = useRef(0);

  const showComposerNotice = useCallback((message) => {
    setComposerNotice(message);
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setComposerNotice(''), 2400);
  }, []);

  const loadConversations = useCallback(async (preferredConversationId = null) => {
    setLoadingConversations(true);
    setError('');

    try {
      const res = await api.get('/messages/conversations');
      const nextContacts = (res.data.conversations || [])
        .map(conversationToContact)
        .filter(contact => contact.conversationId);

      setContacts(nextContacts);
      setActive(prev => {
        const preferred = preferredConversationId
          ? nextContacts.find(contact => contact.conversationId === Number(preferredConversationId))
          : null;
        if (preferred) return preferred;
        if (prev) {
          return nextContacts.find(contact => contact.conversationId === prev.conversationId) || nextContacts[0] || null;
        }
        return nextContacts[0] || null;
      });

      return nextContacts;
    } catch (err) {
      setError(apiMessage(err, text.loadFailed));
      return null;
    } finally {
      setLoadingConversations(false);
    }
  }, [text.loadFailed]);

  const loadMessages = useCallback(async (contact) => {
    if (!contact?.conversationId) return;
    setLoadingMessages(true);
    setError('');

    try {
      const res = await api.get(`/messages/conversations/${contact.conversationId}/messages`);
      const rawMessages = res.data.messages || [];
      const messages = rawMessages.map(row => messageToBubble(row, user?.id));
      for (const row of rawMessages) cursorRef.current = Math.max(cursorRef.current, Number(row.id || 0));
      setMessagesByConversation(prev => ({
        ...prev,
        [contact.conversationId]: messages,
      }));
    } catch (err) {
      setError(apiMessage(err, text.loadFailed));
    } finally {
      setLoadingMessages(false);
    }
  }, [text.loadFailed, user?.id]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const intent = queryToPeerIntent(location.search);

    if (intent.type === 'none') {
      setPageNotice('');
      return undefined;
    }

    if (intent.type === 'display-only') {
      setPageNotice(text.displayOnlyNotice(intent.peerName));
      return undefined;
    }

    let cancelled = false;
    setPageNotice('');

    const openConversation = async () => {
      try {
        if (intent.type === 'support') {
          const res = await api.post('/messages/support/conversation');
          if (cancelled) return;

          const contact = conversationToContact({
            ...res.data,
            support: true,
          });
          setContacts(prev => {
            if (!contact.conversationId) return prev;
            const exists = prev.some(item => item.conversationId === contact.conversationId);
            if (exists) {
              return prev.map(item => item.conversationId === contact.conversationId ? { ...item, ...contact } : item);
            }
            return [contact, ...prev];
          });

          if (contact.conversationId) {
            setActive(contact);
            loadConversations(contact.conversationId);
          }
          return;
        }

        const body = { peerId: intent.peerId };
        if (intent.taskId) body.taskId = intent.taskId;

        const res = await api.post('/messages/conversations', body);
        if (cancelled) return;

        const contact = conversationToContact({
          ...res.data,
          peer_id: intent.peerId,
          peer_name: intent.peerName || res.data.peer_name,
        });

        setContacts(prev => {
          if (!contact.conversationId) return prev;
          const exists = prev.some(item => item.conversationId === contact.conversationId);
          if (exists) {
            return prev.map(item => item.conversationId === contact.conversationId ? { ...item, ...contact } : item);
          }
          return [contact, ...prev];
        });

        if (contact.conversationId) {
          setActive(contact);
          loadConversations(contact.conversationId);
        }
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, text.createFailed));
      }
    };

    openConversation();

    return () => {
      cancelled = true;
    };
  }, [loadConversations, loadMessages, location.search, text]);

  useEffect(() => {
    if (active?.conversationId && !messagesByConversation[active.conversationId]) {
      loadMessages(active);
    }
  }, [active, loadMessages, messagesByConversation]);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const appendRealtimeMessage = useCallback((row) => {
    if (!row?.conversation_id) return;
    cursorRef.current = Math.max(cursorRef.current, Number(row.id || 0));
    const conversationId = Number(row.conversation_id);
    const bubble = messageToBubble(row, user?.id);

    setMessagesByConversation(prev => {
      const existing = prev[conversationId] || [];
      return {
        ...prev,
        [conversationId]: appendUniqueMessage(existing, bubble),
      };
    });

    setContacts(prev => prev.map(contact => (
      contact.conversationId === conversationId
        ? {
            ...contact,
            lastMsg: row.body || contact.lastMsg,
            time: bubble.time || contact.time,
            unread: Number(row.receiver_id) === Number(user?.id) && activeRef.current?.conversationId !== conversationId
              ? Number(contact.unread || 0) + 1
              : contact.unread,
          }
        : contact
    )));
  }, [user?.id]);

  const handleRealtimeEvent = useCallback((event) => {
    if (event?.type === 'message.created') {
      appendRealtimeMessage(event.payload?.message);
      loadConversations(activeRef.current?.conversationId || null);
    }
    if (event?.type === 'message.read') {
      loadConversations(activeRef.current?.conversationId || null);
    }
  }, [appendRealtimeMessage, loadConversations]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    return connectMessagesRealtime({
      token,
      onEvent: handleRealtimeEvent,
    });
  }, [handleRealtimeEvent]);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      try {
        const res = await api.get('/messages/updates', { params: { since: cursorRef.current } });
        (res.data.newMessages || []).forEach(appendRealtimeMessage);
        cursorRef.current = Math.max(cursorRef.current, Number(res.data.cursor || 0));
      } catch {
        // Existing HTTP load and send flows remain available.
      }
    }, 15000);
    return () => window.clearInterval(timer);
  }, [appendRealtimeMessage]);

  useEffect(() => {
    if (msgAreaRef.current) {
      msgAreaRef.current.scrollTop = msgAreaRef.current.scrollHeight;
    }
  }, [active, messagesByConversation]);

  useEffect(() => () => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
  }, []);

  useEffect(() => () => {
    if (screenshotDraft?.previewUrl) URL.revokeObjectURL(screenshotDraft.previewUrl);
  }, [screenshotDraft?.previewUrl]);

  const filteredContacts = contacts.filter(c => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.lastMsg || '').toLowerCase().includes(q);
  });

  const selectContact = (contact) => {
    setActive(contact);
    setContacts(prev => prev.map(item => (
      item.conversationId === contact.conversationId ? { ...item, unread: 0 } : item
    )));
    api.post(`/messages/conversations/${contact.conversationId}/read`)
      .catch(err => setError(apiMessage(err, text.loadFailed)));
  };

  const send = async (contentOverride = null) => {
    const hasOverride = typeof contentOverride === 'string';
    const content = hasOverride ? contentOverride.trim() : input.trim();
    if (!content || !active?.conversationId || sending) return;

    setSending(true);
    setError('');

    try {
      const res = await api.post(`/messages/conversations/${active.conversationId}/messages`, { body: content });
      const message = messageToBubble(res.data, user?.id);
      cursorRef.current = Math.max(cursorRef.current, Number(res.data.id || 0));
      setMessagesByConversation(prev => ({
        ...prev,
        [active.conversationId]: appendUniqueMessage(prev[active.conversationId] || [], message),
      }));
      setContacts(prev => prev.map(contact => (
        contact.conversationId === active.conversationId
          ? { ...contact, lastMsg: content, time: message.time, unread: 0 }
          : contact
      )));
      if (!hasOverride) setInput('');
      loadConversations(active.conversationId);
    } catch (err) {
      setError(apiMessage(err, text.sendFailed));
    } finally {
      setSending(false);
    }
  };

  const insertEmoji = (emoji) => {
    setInput(prev => `${prev}${emoji}`);
    setShowEmoji(false);
  };

  const uploadAndSend = async (file, kind) => {
    if (!file || !active?.conversationId || uploadingAttachment) return false;

    setUploadingAttachment(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('purpose', 'message_attachment');
      const res = await api.post('/uploads', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const asset = res.data.asset;
      if (!asset?.url) throw new Error(text.uploadFailed);

      const factory = kind === 'file'
        ? text.fileMessage
        : kind === 'screenshot'
          ? text.screenshotMessage
          : text.imageMessage;
      await send(factory(file.name, asset.url));
      return true;
    } catch (err) {
      showComposerNotice(apiMessage(err, text.uploadFailed));
      return false;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleComposerFile = async (event, kind) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    await uploadAndSend(file, kind);
  };

  const closeScreenshotCrop = () => {
    setScreenshotDraft(null);
    setScreenshotCrop(DEFAULT_SCREENSHOT_CROP);
    cropDragRef.current = null;
  };

  const resetScreenshotCrop = () => {
    setScreenshotCrop(DEFAULT_SCREENSHOT_CROP);
    cropDragRef.current = null;
  };

  const captureScreenshot = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      showComposerNotice(text.screenCaptureFailed);
      return;
    }

    let stream = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const video = document.createElement('video');
      video.muted = true;
      video.srcObject = stream;

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
        const playResult = video.play();
        if (playResult?.catch) playResult.catch(reject);
      });

      await new Promise(resolve => window.requestAnimationFrame(() => resolve()));

      const width = video.videoWidth || 1280;
      const height = video.videoHeight || 720;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error(text.screenCaptureFailed);
      context.drawImage(video, 0, 0, width, height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(nextBlob => (
          nextBlob ? resolve(nextBlob) : reject(new Error(text.screenCaptureFailed))
        ), 'image/png');
      });

      const previewUrl = URL.createObjectURL(blob);
      setScreenshotDraft({
        blob,
        previewUrl,
        width,
        height,
        name: `screenshot-${Date.now()}.png`,
      });
      resetScreenshotCrop();
    } catch {
      showComposerNotice(text.screenCaptureFailed);
    } finally {
      if (stream) stream.getTracks().forEach(track => track.stop());
    }
  };

  const getCropPoint = (event) => {
    const rect = cropStageRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return null;
    return {
      x: clampUnit((event.clientX - rect.left) / rect.width),
      y: clampUnit((event.clientY - rect.top) / rect.height),
    };
  };

  const handleCropPointerDown = (event) => {
    const point = getCropPoint(event);
    if (!point) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    cropDragRef.current = point;
    setScreenshotCrop({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const handleCropPointerMove = (event) => {
    const dragStart = cropDragRef.current;
    if (!dragStart) return;
    const point = getCropPoint(event);
    if (!point) return;
    setScreenshotCrop(cropFromPoints(dragStart, point));
  };

  const handleCropPointerUp = (event) => {
    const dragStart = cropDragRef.current;
    if (!dragStart) return;
    const point = getCropPoint(event);
    if (point) setScreenshotCrop(cropFromPoints(dragStart, point));
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    cropDragRef.current = null;
  };

  const submitScreenshotCrop = async () => {
    if (!screenshotDraft || uploadingAttachment) return;

    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = screenshotDraft.previewUrl;
      });

      const sourceWidth = image.naturalWidth || screenshotDraft.width;
      const sourceHeight = image.naturalHeight || screenshotDraft.height;
      const crop = screenshotCrop.width > 0.01 && screenshotCrop.height > 0.01
        ? screenshotCrop
        : DEFAULT_SCREENSHOT_CROP;
      const sx = Math.round(crop.x * sourceWidth);
      const sy = Math.round(crop.y * sourceHeight);
      const sw = Math.max(1, Math.round(crop.width * sourceWidth));
      const sh = Math.max(1, Math.round(crop.height * sourceHeight));
      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const context = canvas.getContext('2d');
      if (!context) throw new Error(text.screenCaptureFailed);
      context.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(nextBlob => (
          nextBlob ? resolve(nextBlob) : reject(new Error(text.screenCaptureFailed))
        ), 'image/png');
      });
      const file = new File([blob], screenshotDraft.name, { type: 'image/png' });
      const sent = await uploadAndSend(file, 'screenshot');
      if (sent) closeScreenshotCrop();
    } catch {
      showComposerNotice(text.screenCaptureFailed);
    }
  };

  const sendLocation = async () => {
    if (!navigator.geolocation?.getCurrentPosition) {
      showComposerNotice(text.locationFailed);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        });
      });
      const { latitude, longitude } = position.coords;
      const url = `https://maps.google.com/?q=${latitude.toFixed(6)},${longitude.toFixed(6)}`;
      const address = await reverseGeocodeLocation(latitude, longitude, language);
      await send(text.locationMessage(url, address));
    } catch {
      showComposerNotice(text.locationFailed);
    }
  };

  const handleComposerTool = (tool) => {
    if (uploadingAttachment || sending) return;
    if (tool.type === 'smile') {
      setShowEmoji(prev => !prev);
      return;
    }
    setShowEmoji(false);
    if (tool.type === 'image') imageInputRef.current?.click();
    if (tool.type === 'scissors') captureScreenshot();
    if (tool.type === 'clipboard') fileInputRef.current?.click();
    if (tool.type === 'pin') sendLocation();
  };

  const renderLocationCard = (locationInfo) => {
    const title = locationInfo.address || text.locationCardTitle;
    const coords = Number.isFinite(locationInfo.lat) && Number.isFinite(locationInfo.lng)
      ? `${locationInfo.lat.toFixed(5)}, ${locationInfo.lng.toFixed(5)}`
      : text.locationCardHint;
    const subtitle = locationInfo.address ? text.locationCardHint : coords;

    return (
      <a className="msg-location-card" href={locationInfo.url} target="_blank" rel="noreferrer">
        <div className="msg-location-copy">
          <strong>{title}</strong>
          <span>{subtitle}</span>
          {locationInfo.address && <em>{text.locationAttribution}</em>}
        </div>
        <div className="msg-location-map" aria-hidden="true">
          <span className="msg-location-road msg-location-road-a" />
          <span className="msg-location-road msg-location-road-b" />
          <span className="msg-location-road msg-location-road-c" />
          <span className="msg-location-block msg-location-block-a" />
          <span className="msg-location-block msg-location-block-b" />
          <span className="msg-location-pin" />
        </div>
      </a>
    );
  };

  const renderAttachmentPreview = (attachment) => (
    <a className="msg-attachment-preview" href={attachment.url} target="_blank" rel="noreferrer">
      <span className="msg-attachment-title">
        <strong>{attachment.label}</strong>
        <em>{attachment.name}</em>
      </span>
      {attachment.preview ? (
        <img src={attachment.url} alt={attachment.name} className="msg-attachment-image" loading="lazy" />
      ) : (
        <span className="msg-attachment-file">{attachment.url}</span>
      )}
    </a>
  );

  const renderMessageContent = (value) => {
    const translated = String(translatePhrase(value) || '');
    const attachment = parseAttachmentMessage(translated) || parseAttachmentMessage(value);
    if (attachment) return renderAttachmentPreview(attachment);

    const locationInfo = parseLocationMessage(translated) || parseLocationMessage(value);
    if (locationInfo) return renderLocationCard(locationInfo);

    return translated.split(/(https?:\/\/[^\s]+)/g).map((part, index) => (
      /^https?:\/\//.test(part)
        ? <a key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer">{part}</a>
        : part
    ));
  };

  const currentMsgs = active ? (messagesByConversation[active.conversationId] || []) : [];

  const requestReload = () => {
    setReloadConfirmVisible(true);
  };

  const clearAll = () => {
    setReloadConfirmVisible(false);
    setMessagesByConversation({});
    setActive(null);
    loadConversations();
  };

  const confirmSafety = () => {
    if (!safetyChecked) return;
    localStorage.setItem(STORAGE.safetyAck, '1');
    setShowSafety(false);
  };

  const cropSelectionStyle = {
    left: `${screenshotCrop.x * 100}%`,
    top: `${screenshotCrop.y * 100}%`,
    width: `${screenshotCrop.width * 100}%`,
    height: `${screenshotCrop.height * 100}%`,
  };
  const screenshotCropVisible = screenshotCrop.width > 0.002 || screenshotCrop.height > 0.002;

  return (
    <div className="msg-page">
      <div className="msg-sidebar">
        <div className="msg-sb-head">
          <h3>{text.title}</h3>
          <div className="msg-sb-actions">
            <Link to="/messages?support=1" className="msg-support-link" title={text.contactSupport}>
              {text.contactSupport}
            </Link>
            <button
              className="msg-clear"
              onClick={requestReload}
              title={text.reset}
              aria-label={text.reset}
              aria-expanded={reloadConfirmVisible}
            >
              <ComposerIcon type="clipboard" />
            </button>
          </div>
        </div>
        {reloadConfirmVisible && (
          <div className="msg-reload-confirm" role="status">
            <span>{text.clearConfirm}</span>
            <div>
              <button type="button" onClick={clearAll}>{text.reloadNow}</button>
              <button type="button" onClick={() => setReloadConfirmVisible(false)}>{text.cancel}</button>
            </div>
          </div>
        )}
        <input
          type="text"
          placeholder={text.search}
          className="msg-search"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
        />
        {error && <div className="msg-inline-notice" role="status">{error}</div>}
        <div className="msg-contact-list">
          {loadingConversations ? (
            <div className="msg-no-contact">{text.loading}</div>
          ) : filteredContacts.length === 0 ? (
            <div className="msg-no-contact">{text.noContact}</div>
          ) : (
            filteredContacts.map(c => (
              <div
                key={c.conversationId}
                className={`msg-contact ${active?.conversationId === c.conversationId ? 'active' : ''}`}
                onClick={() => selectContact(c)}
              >
                <img src={c.avatar} alt={c.name} className="msg-avatar" />
                <div className="msg-contact-info">
                  <div className="msg-contact-row1">
                    <strong>{c.name}</strong>
                    <span className="msg-contact-time">{c.time}</span>
                  </div>
                  <div className="msg-contact-row2">{translatePhrase(c.lastMsg)}</div>
                </div>
                <div className="msg-contact-thumb" aria-hidden="true">{(c.deal || c.name).slice(0, 1)}</div>
                {c.unread > 0 && <span className="msg-unread">{c.unread}</span>}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="msg-main">
        {pageNotice && <div className="msg-page-notice" role="status">{pageNotice}</div>}
        {active ? (
          <>
            <div className="msg-chat-head">
              <div className="msg-chat-title">
                <img src={active.avatar} alt="" className="msg-avatar-sm" />
                <div>
                  <strong>{active.name}</strong>
                  <span>{active.isSupport ? text.supportChat : text.verified}</span>
                </div>
              </div>
              <div className="msg-chat-actions">
                <span className="msg-chat-status">{text.dealStatus}</span>
              </div>
            </div>
            <div className="msg-area" ref={msgAreaRef}>
              <div className="msg-deal-card">
                <div className="msg-deal-thumb" aria-hidden="true">{(active.deal || active.name).slice(0, 1)}</div>
                <div>
                  <strong>{translatePhrase(active.deal || active.name)}</strong>
                  {active.price && <span>{active.price}</span>}
                </div>
              </div>
              {loadingMessages && <div className="msg-area-status">{text.loadingMessages}</div>}
              {!loadingMessages && currentMsgs.length === 0 && <div className="msg-area-status">{text.noMessages}</div>}
              {currentMsgs.map(m => (
                <div key={m.id} className={`msg-row ${m.fromMe ? 'me' : 'them'}`}>
                  {!m.fromMe && <img src={active.avatar} alt="" className="msg-bubble-avatar" />}
                  <div className="msg-bubble">{renderMessageContent(m.text)}<div className="msg-bubble-time">{m.time}</div></div>
                </div>
              ))}
            </div>
            <div className="msg-composer">
              <div className="msg-composer-tools" aria-label={text.toolAria}>
                {COMPOSER_TOOLS.map(tool => (
                  <button
                    className="msg-tool-btn"
                    type="button"
                    title={text.tools[tool.label] || tool.label}
                    aria-label={text.tools[tool.label] || tool.label}
                    key={tool.type}
                    onClick={() => handleComposerTool(tool)}
                    disabled={uploadingAttachment || sending}
                  >
                    <ComposerIcon type={tool.type} />
                  </button>
                ))}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={event => handleComposerFile(event, 'image')}
              />
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,application/pdf,text/plain,application/zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={event => handleComposerFile(event, 'file')}
              />
              {showEmoji && (
                <div className="msg-emoji-panel" role="menu">
                  {emojiOptions.map(emoji => (
                    <button key={emoji} type="button" onClick={() => insertEmoji(emoji)}>{emoji}</button>
                  ))}
                </div>
              )}
              {uploadingAttachment && <div className="msg-composer-notice" role="status">{text.uploadingAttachment}</div>}
              {composerNotice && <div className="msg-composer-notice" role="status">{composerNotice}</div>}
              <textarea
                className="msg-composer-input"
                placeholder={text.input}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button className="msg-composer-send" type="button" disabled={!input.trim() || sending || uploadingAttachment} onClick={send}>
                {sending ? text.sending : text.send}
              </button>
            </div>
          </>
        ) : (
          <div className="msg-empty">
            <div>💬</div>
            <p>{loadingConversations ? text.loading : text.empty}</p>
          </div>
        )}
      </div>

      {screenshotDraft && (
        <div className="msg-crop-overlay" role="dialog" aria-modal="true" aria-labelledby="msg-crop-title">
          <div className="msg-crop-modal">
            <div className="msg-crop-head">
              <h3 id="msg-crop-title">{text.cropTitle}</h3>
              <button type="button" onClick={closeScreenshotCrop} aria-label={text.cancel}>×</button>
            </div>
            <div
              className="msg-crop-stage"
              ref={cropStageRef}
              onPointerDown={handleCropPointerDown}
              onPointerMove={handleCropPointerMove}
              onPointerUp={handleCropPointerUp}
              onPointerCancel={handleCropPointerUp}
            >
              <img src={screenshotDraft.previewUrl} alt="" draggable="false" />
              <span className="msg-crop-shade" />
              <span
                className={screenshotCropVisible ? 'msg-crop-selection visible' : 'msg-crop-selection'}
                style={cropSelectionStyle}
              >
                <span className="msg-crop-handle" />
                <span className="msg-crop-handle" />
                <span className="msg-crop-handle" />
                <span className="msg-crop-handle" />
              </span>
            </div>
            <div className="msg-crop-actions">
              <button type="button" onClick={closeScreenshotCrop}>{text.cancel}</button>
              <button type="button" onClick={captureScreenshot}>{text.cropRetake}</button>
              <button type="button" onClick={submitScreenshotCrop} disabled={uploadingAttachment}>
                {uploadingAttachment ? text.uploadingAttachment : text.sendScreenshot}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSafety && (
        <div className="msg-safety-overlay" role="dialog" aria-modal="true" aria-labelledby="msg-safety-title">
          <div className="msg-safety-modal">
            <div className="msg-safety-art" aria-hidden="true">
              <div className="safe-card-window">
                <span />
                <span />
                <span />
                <div className="safe-user" />
              </div>
              <div className="safe-shield" aria-hidden="true">✓</div>
            </div>
            <div className="msg-safety-copy">
              <h2 id="msg-safety-title">{text.safetyTitle}</h2>

              <p>{text.safetyIntro}</p>
              <p>{text.safetyPay}</p>
              <p>{text.safetyPenalty}</p>

              <Link to="/about" className="msg-safety-learn">{text.learnMore}</Link>

              <label className="msg-safety-check">
                <input type="checkbox" checked={safetyChecked} onChange={e => setSafetyChecked(e.target.checked)} />
                <span>{text.agreement}</span>
              </label>

              <button className="msg-safety-confirm" type="button" disabled={!safetyChecked} onClick={confirmSafety}>
                {text.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
